import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'app-question-with-options',
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './question-with-options.component.html',
  styleUrl: './question-with-options.component.css',
})
export class QuestionWithOptionsComponent {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  fieldsData: any;
  selectedItem: any = null;
  selectedOptions: any[] = [];
  selectedValue: any;
  showError: boolean = false;
  selectedValues: any;
  showErrors: string = '';
  @Output() handleTaskMappingDependency = new EventEmitter<{
    fieldName: string;
    tags: any[];
    selectedQuestion: any;
  }>();
  constructor(private fb: FormBuilder) {}
  ngOnInit() {
    this.getData();
    this.initializeData();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['fields'] && this.fields && this.fields.value) {
      this.initializeData();
    }
  }

  get control(): FormControl {
    return this.formGroup.get(this.fields.fieldName) as FormControl;
  }

  initializeData() {
    const control = this.formGroup.get(this.fields.fieldName);
    if (this.fields && this.fields?.value) {
      if (
        this.fields.fieldType === 'radio' ||
        this.fields.fieldType === 'singleSelect'
      ) {
        this.selectedValue = this.fields.value;
        this.restoreTagsFromSelectedOptions();
        setTimeout(() => {
          if (
            control &&
            this.fields?.value !== undefined &&
            this.fields?.value !== null &&
            this.fields?.value !== ''
          ) {
            control.setValue(this.fields.value, { emitEvent: false });
          }
        }, 0);

        // Subscribe to changes to sync value and hide error
        control?.valueChanges.subscribe((val) => {
          this.fields.value = val;
          this.showError = false;
        });
      }

      if (this.fields.fieldType === 'multiSelect') {
        const value = this.fields.value;
        if (Array.isArray(value)) {
          control?.patchValue([...value]);
        }
      }

      if (this.fields.fieldType === 'tableRow') {
        const value = this.fields.value;
        if (Array.isArray(value)) {
          control?.patchValue([...value]);
        }
      }

      if (
        this.fields.fieldType === 'checkbox' ||
        this.fields.fieldType === 'checkboxButton'
      ) {
        const control = this.formGroup.get(this.fields.fieldName);

        // Normalize the incoming value: convert comma string to array if needed
        let initial: string[] = [];

        if (typeof this.fields.value === 'string') {
          initial = this.fields.value.split(',').map((v: any) => v.trim());
        } else if (Array.isArray(this.fields.value)) {
          initial = this.fields.value.map((v: any) => v.toString());
        }

        this.selectedOptions = [...initial];

        control?.setValue(this.selectedOptions, { emitEvent: false });
        control?.markAsTouched();
        control?.updateValueAndValidity();
      }
      this.restoreTagsFromSelectedOptions();
    }
  }

  /** Function to get the data by api or static */
  getData() {
    if (this.fields.options?.isStatic) {
      this.fieldsData = this.fields.options.staticOptions || [];
      this.restoreTagsFromSelectedOptions();
      setTimeout(() => {
        const control = this.formGroup.get(this.fields.fieldName);
        if (this.fields.value != null) {
          control?.patchValue(this.fields.value.toString());
        }
      }, 10);
    }

    // Special handling for tableRow
    if (
      this.fields.fieldType === 'tableRow' &&
      this.fields.options?.staticOptions?.length
    ) {
      this.fields.options.staticOptions.forEach((item: any) => {
        if (!this.formGroup.contains(item.fieldName)) {
          this.formGroup.addControl(item.fieldName, new FormControl(false));
        }
      });
    }
  }

  isSelected(item: any): boolean {
    const control = this.formGroup.get(this.fields.fieldName);
    const selected = control?.value || [];
    const value = item.value ?? item._id ?? item.text;
    return Array.isArray(selected) && selected.includes(value);
  }

  /** Update the form control value */
  selectItem(item: any): void {
    const fieldName = this.resolveFieldName(this.fields); // ✅ safer

    const control = this.formGroup.get(fieldName);
    const tagsControl = this.formGroup.get(fieldName + 'Tags');

    if (!control || !tagsControl) return;

    const optionValue = (item.value ?? item.id ?? item.text)?.toString();
    const optionName = item.name ?? item.label ?? item.text ?? '';

    let selected = [...this.selectedOptions];
    let selectedTags = Array.isArray(tagsControl.value)
      ? [...tagsControl.value]
      : [];

    if (selected.includes(optionValue)) {
      selected = selected.filter((val) => val !== optionValue);
      selectedTags = selectedTags.filter((tag: any) => tag !== optionName);
    } else {
      selected.push(optionValue);
      selectedTags.push(optionName);
    }

    this.selectedOptions = [...selected];

    control.setValue(
      this.selectedOptions.length ? this.selectedOptions : null,
      { emitEvent: false }, // ✅ prevent duplicate triggers
    );
    tagsControl?.setValue([], { emitEvent: false });
    tagsControl.setValue(selectedTags, { emitEvent: false });

    control.markAsTouched();
    control.updateValueAndValidity();

    // ✅ validation
    if (
      this.fields.isRequired &&
      (!this.selectedOptions || this.selectedOptions.length === 0)
    ) {
      this.showErrors = 'Please select the option';
    } else {
      this.showErrors = '';
    }

    // ✅ 🔥 EMIT EVENT (IMPORTANT)
    this.handleTaskMappingDependency.emit({
      fieldName,
      tags: selectedTags,
      selectedQuestion:
        fieldName === 'phisingTestOptionsField'
          ? 'Do you periodically conduct phishing test'
          : this.fields.displayName || this.fields.helpText || '',
    });
  }

  onMultiSelectChange(event: any, fields: any) {
    const fieldName = this.resolveFieldName(fields); // ✅ safer

    const control = this.formGroup.get(fieldName);
    const tagsControl = this.formGroup.get(fieldName + 'Tags');

    if (!control || !tagsControl) return;

    let selectedValues = Array.isArray(control.value) ? [...control.value] : [];
    let selectedTags = Array.isArray(tagsControl.value)
      ? [...tagsControl.value]
      : [];

    const value = event.target.value;

    const option = this.fieldsData?.find(
      (opt: any) => (opt.value ?? opt.id ?? opt.text)?.toString() === value,
    );

    const optionName = option?.name ?? option?.label ?? option?.text ?? value;

    if (event.target.checked) {
      if (!selectedValues.includes(value)) {
        selectedValues.push(value);
        selectedTags.push(optionName);
      }
    } else {
      const index = selectedValues.indexOf(value);
      if (index !== -1) {
        selectedValues.splice(index, 1);
        selectedTags.splice(index, 1);
      }
    }

    control.setValue(selectedValues, { emitEvent: false }); // ✅ prevent extra triggers
    // 🔥 clear previous first
    tagsControl?.setValue([], { emitEvent: false });

    tagsControl.setValue(selectedTags, { emitEvent: false });

    control.markAsTouched();
    control.updateValueAndValidity();

    // ✅ 🔥 EMIT HERE (IMPORTANT)
    this.handleTaskMappingDependency.emit({
      fieldName,
      tags: selectedTags,
      selectedQuestion:
        fieldName === 'phisingTestOptionsField'
          ? 'Do you periodically conduct phishing test'
          : this.fields.displayName || this.fields.helpText || '',
    });
  }

  /** Function to trigger on selection of radio button */
  onRadioChange(value: any) {
    this.selectedValue = value;

    const fieldName = this.resolveFieldName(this.fields);
    const control = this.formGroup.get(fieldName);
    const tagsControl = this.formGroup.get(fieldName + 'Tags');

    if (control) {
      control.setValue(value);
      control.markAsTouched();
      control.updateValueAndValidity();

      let tagValue = '';

      if (this.fieldsData?.length) {
        const selectedOption = this.fieldsData.find(
          (opt: any) =>
            opt.value === value || opt.id === value || opt.text === value,
        );

        tagValue =
          selectedOption?.name ||
          selectedOption?.label ||
          selectedOption?.text ||
          '';
        tagsControl?.setValue([], { emitEvent: false });
        tagsControl?.setValue(tagValue ? [tagValue] : [], {
          emitEvent: false,
        });
      }

      // ✅ FIXED
      this.handleTaskMappingDependency.emit({
        fieldName,
        tags: tagValue ? [tagValue] : [],
        selectedQuestion:
          fieldName === 'phisingTestOptionsField'
            ? 'Do you periodically conduct phishing test'
            : this.fields.displayName || this.fields.helpText || '',
      });
    }
  }

  restoreTagsFromSelectedOptions(): void {
    const control = this.formGroup.get(this.fields.fieldName);
    const tagsControl = this.formGroup.get(this.fields.fieldName + 'Tags');

    if (!control || !tagsControl || !this.fieldsData?.length) return;
    if (this.fields.fieldType == 'radio') {
      this.selectedValues = this.selectedValue;
    } else {
      this.selectedValues = control.value;
    }

    let selectedTags: string[] = [];
    if (Array.isArray(this.selectedValues)) {
      // For multi-select / checkbox
      selectedTags = this.fieldsData
        .filter((opt: any) =>
          this.selectedValues.includes(
            (opt.value ?? opt.id ?? opt.text)?.toString(),
          ),
        )
        .map((opt: any) => opt.name ?? opt.label ?? opt.text ?? '');
    } else {
      // For radio / single select
      const selectedOption = this.fieldsData.find(
        (opt: any) =>
          (opt.value ?? opt.id ?? opt.text)?.toString() ===
          this.selectedValues?.toString(),
      );

      if (selectedOption) {
        selectedTags = [
          selectedOption.name ??
            selectedOption.label ??
            selectedOption.text ??
            '',
        ];
      }
    }
    tagsControl.setValue(selectedTags);

    const fieldName =
      this.resolveFieldName(this.fields) || this.fields.fieldName;
    this.handleTaskMappingDependency.emit({
      fieldName: fieldName,
      tags: selectedTags,
      selectedQuestion:
        fieldName === 'phisingTestOptionsField'
          ? 'Do you periodically conduct phishing test'
          : this.fields.displayName || this.fields.helpText || '',
    });
  }

  showErrorMessage(): boolean {
    const control = this.formGroup.get(this.fields.fieldName);

    if (this.fields.isRequired) {
      control?.markAsTouched();

      if (!this.selectedOptions || this.selectedOptions.length === 0) {
        this.showErrors = 'Please select the option';
        return false; // Return false to indicate INVALID
      } else {
        this.showErrors = '';
        return true; // Return true to indicate VALID
      }
    }

    return true; // Not required, so valid by default
  }

  getColumnClass(text: string, index: number): string {
    const lower = text?.toLowerCase();
    if (index === 0 || lower.includes('domain')) return 'col col-domain';
    if (lower.includes('email')) return 'col col-email';
    if (lower.includes('spf')) return 'col col-spf';
    return 'col col-icon';
  }

  private resolveFieldName(field: any): string {
    if (!field) return '';

    // ✅ Case 1: direct string
    if (typeof field.fieldName === 'string') {
      return field.fieldName;
    }

    // ✅ Case 2: object { label, value }
    if (field.fieldName?.label) {
      return field.fieldName.label;
    }

    // ✅ Case 3: value[] format
    if (Array.isArray(field)) {
      return this.getValueByKey(field, 'fieldName') || '';
    }

    // ✅ Case 4: field.value is array (very common in your setup)
    if (Array.isArray(field.value)) {
      return this.getValueByKey(field.value, 'fieldName') || '';
    }

    return '';
  }

  private getValueByKey(arr: any[], key: string): any {
    return (arr || []).find((item: any) => item.Key === key)?.Value;
  }
}
